import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Map as MapIcon, PackageSearch, UserRound } from 'lucide-react';
import { NpcCard } from '../progression/NpcCard';
import { ValidationBadge } from '../progression/ValidationBadge';
import { EntityThumb } from './EntityThumb';
import { itemName, itemPath, mapPath, monsterPath } from '../../utils/worldFormat';
import { locationPoints, priceLabel, publishedShopItems, serviceLabel, shopOf } from '../../utils/npcShops';
import { NPC_PATH } from '../../utils/npcShops';
import type { NpcLocation } from '../../utils/npcLocation';
import type { Npc } from '../../types/progression';
import type { WorldData } from '../../types/world';
const btn = 'inline-flex h-9 items-center gap-1.5 rounded-xl border border-white/10 px-3 text-xs font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/40 hover:text-white';

/** Ficha de NPC en "NPC y tiendas": misma tarjeta de ubicación que el resto de la guía + servicios + tienda. */
export function NpcShopCard({
  npc,
  loc,
  w
}: {
  npc: Npc;
  loc: NpcLocation;
  w?: WorldData;
}) {
  const mapId = locationPoints(loc).find(p => p.mapId !== undefined)?.mapId;
  const shop = shopOf(npc.id);
  const sells = npc.services?.some(s => s.kind === 'venta');
  const items = shop ? publishedShopItems(w, shop) : [];
  const ownGuide = npc.sectionPath.startsWith(NPC_PATH) ? undefined : npc.sectionPath;
  return <article id={npc.id} className="scroll-mt-24 md:scroll-mt-32" data-id="element-1620" data-mp="2kfb54h30rwe5">
      <NpcCard npcId={npc.id} data-id="element-1621" data-mp="2pd47t054689o" />

      <div className="mt-2.5 space-y-3 px-1" data-id="element-1622" data-mp="2pefb6yunde09">
        {npc.identityNote && <p className="rounded-xl bg-amber-300/[0.06] px-3 py-2 text-xs text-amber-100" data-id="element-1623" data-mp="2ucp5z8cgmclc">{npc.identityNote}</p>}

        {npc.services?.length ? <div data-id="element-1624" data-mp="2yflwdl88rczu">
            <p className="mb-1.5 text-xs font-semibold text-gray-400" data-id="element-1625" data-mp="2yby0iffbqehy">Servicios</p>
            <ul className="flex flex-wrap gap-1.5" data-id="element-1626" data-mp="9lopp25s9v4p">
              {npc.services.map(s => <li key={s.kind} className="flex flex-wrap items-center gap-1.5 rounded-xl bg-white/[0.04] px-2.5 py-1.5 text-sm text-white" data-id="element-1627" data-mp="dm8mg4mg2wqb">
                  {serviceLabel[s.kind]}
                  {s.note && <span className="text-xs text-gray-400" data-id="element-1628" data-mp="25ol6kwk8ti6p">· {s.note}</span>}
                  <ValidationBadge status={s.status} label={s.status === 'clasica' ? 'Función clásica' : undefined} data-id="element-1629" data-mp="2jpe2nlq70lbx" />
                </li>)}
            </ul>
          </div> : null}

        {(sells || shop) && <div data-id="element-1630" data-mp="1r0q9z5346toc">
            <p className="mb-1.5 text-xs font-semibold text-gray-400" data-id="element-1631" data-mp="1qydrmqtg2har">Qué vende</p>
            {items.length ? <ul className="grid gap-1.5 sm:grid-cols-2" data-id="element-1632" data-mp="20q9v6heauzmd">
                {items.map(it => <li key={`${it.itemId}-${it.level ?? ''}`} className="flex items-center gap-2.5 rounded-xl bg-white/[0.03] px-2.5 py-2" data-id="element-1633" data-mp="1vzjql6dp70sq">
                    <EntityThumb entity={{
              type: 'item',
              id: it.itemId
            }} name={w ? itemName(w, it.itemId) : it.itemId} placeholder data-id="element-1634" data-mp="188xhrcvvdcho" />
                    <span className="min-w-0 flex-1" data-id="element-1635" data-mp="138tamdgupu44">
                      <Link to={itemPath(it.itemId)} className="block truncate text-sm font-semibold text-white underline decoration-white/20 underline-offset-4 hover:text-[#D4AF37]" data-id="element-1636" data-mp="3exzelvzittub">
                        {w ? itemName(w, it.itemId) : it.itemId}
                      </Link>
                      <span className="block text-xs text-gray-400" data-id="element-1637" data-mp="3awe7kr9o49s1">
                        {[it.level !== undefined ? `+${it.level}` : '', priceLabel(it) ?? 'Precio no documentado'].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </li>)}
              </ul> : <p className="flex items-start gap-2 text-sm text-gray-400" data-id="element-1638" data-mp="3onj0laij63te">
                <PackageSearch className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1639" data-mp="3tmcf969k00v5" />
                Inventario no documentado: los archivos del servidor entregados no incluyen las tiendas.
              </p>}
          </div>}

        <div className="flex flex-wrap gap-2" data-id="element-1640" data-mp="2xhlry0bv2q6x">
          {mapId !== undefined && <Link to={mapPath(mapId)} className={btn} data-id="element-1641" data-mp="2xjq6dc7dz7lg">
              <MapIcon className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1642" data-mp="2spu9ye6rtsse" /> Ver mapa
            </Link>}
          {npc.monsterId !== undefined && <Link to={monsterPath(npc.monsterId)} className={btn} data-id="element-1643" data-mp="2jqgzbi6wuy63">
              <UserRound className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1644" data-mp="2jo48sgn37fhf" /> Ver ficha
            </Link>}
          {ownGuide && <Link to={ownGuide} className={btn} data-id="element-1645" data-mp="3cemkg61vmp6g">
              <BookOpen className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1646" data-mp="3ccrxkm71omzq" /> Ver guía
            </Link>}
        </div>
      </div>
    </article>;
}